import axios from 'axios'
import { getUiServicesEndpoint } from './utils'

import {
  ShipmentRowInterface,
  ShipmentModificationVariables,
} from '../common/types'
import dayjs from 'dayjs'
import { pickupDateChangeReasons } from 'constants/pickupDateChangeReason'

export const transformShipments = (shipments: Array<ShipmentRowInterface>) => {
  return {
    modification_requests: shipments.map((shipment: ShipmentRowInterface) => {
      const {
        appointmentNumber,
        bookingId,
        bookingVersion,
        bol,
        carton,
        commodityCode,
        loadId,
        origLocCode,
        pallet,
        pickupDate,
        pickupReasonCode,
        toBeCancelled,
        totalVol,
        totalWt,
        volUom,
        wtUom,
        vop,
        tloRefId,
        trailerId,
        sealNumber,
        reverseLogisticsReferenceNumber,
        returnAuthNumber,
      } = shipment

      if (toBeCancelled) {
        return {
          load_id: loadId,
          booking_modification_request: {
            id: bookingId,
            version: bookingVersion,
            change_reason: 'vendor change',
            action: 'CANCEL',
          },
        }
      }

      const hasPickupOverride =
        !!pickupReasonCode &&
        pickupReasonCode !==
          pickupDateChangeReasons.TGT_DRIVEN_PICKUP_DATE_OVERRIDE.label

      return {
        load_id: loadId,
        booking_modification_request: {
          action: 'MODIFY',
          id: bookingId,
          version: bookingVersion,
          modified_attributes: {
            origin_location: {
              code: origLocCode,
              vop,
            },
            truck_load_optimization_ref_id: tloRefId,
            commodity_code: commodityCode,
            trailer_id: trailerId,
            seal_number: sealNumber,
            reverse_logistics_reference_number: reverseLogisticsReferenceNumber,
            return_authorization_number: returnAuthNumber,
            measures: {
              carton_quantity: carton,
              pallet: pallet,
              weight: {
                total: totalWt,
                unit_of_measure: wtUom,
              },
              volume: {
                total: totalVol,
                unit_of_measure: volUom,
              },
            },
            ...(hasPickupOverride && {
              pickup_override: {
                date: dayjs(pickupDate).format('YYYY-MM-DD'),
                change_reason: pickupReasonCode,
              },
            }),
            vendor_release: 'VENDOR_ACKNOWLEDGED',
            appointment_number: appointmentNumber,
            bill_of_lading: bol,
          },
        },
      }
    }),
  }
}

export const shipmentsLoadModificationsEndpoint = async ({
  shipments,
  uid,
}: ShipmentModificationVariables) => {
  const modifiedShipments = shipments.filter((shipment: any) => {
    return shipment?.loadId
  })

  const transformedShipments = transformShipments(modifiedShipments)

  if (uid <= 0) {
    return
  }
  if (modifiedShipments.length <= 0) {
    return
  }
  const { key, url } = await getUiServicesEndpoint()
  const endpoint = `${url}/v1/load_modifications?key=${key}`

  try {
    const response = await axios({
      method: 'post',
      url: endpoint,
      data: transformedShipments,
    })

    const failures = ((response?.data as any)?.failures || []).map(
      (failure: any) => {
        return {
          booking_id: failure.booking_id,
          status: failure.status,
        }
      },
    )
    const failedBookingIds = failures.map((failure: any) => failure.booking_id)
    const failedShipments = modifiedShipments
      .filter((shipment: any) => failedBookingIds.includes(shipment.bookingId))
      .map((shipment: any) => {
        const failure = failures.find(
          (f: any) => f.booking_id === shipment.bookingId,
        )
        return {
          ...shipment,
          failureStatus: failure?.status,
        }
      })
    return failedShipments
    // eslint-disable-next-line
  } catch (err: any) {
    return modifiedShipments.map((shipment: any) => ({
      ...shipment,
      failureStatus: 'ERROR',
    }))
  }
}
